import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { routeTree } from './routeTree.gen';
// Шрифты — в бандл через @fontsource (не зависим от Google CDN: критично для клиентов
// в РФ, где fonts.googleapis.com часто блокируется). Заголовки Geologica объявлены в
// index.css своими @font-face, как на сайте.
import '@fontsource/manrope/400.css';
import '@fontsource/manrope/500.css';
import '@fontsource/manrope/700.css';
import '@fontsource/manrope/800.css';
import '@fontsource/jetbrains-mono/400.css';
import '@fontsource/jetbrains-mono/500.css';
import '@fontsource/jetbrains-mono/600.css';
import './index.css';
import './i18n';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { queryClient } from '@/lib/query-client';
import { api } from '@/lib/api';
import { MotionConfig } from 'motion/react';

// Sentry lazy-load: SDK ~50KB. Загружаем только если DSN сконфигурен и после
// первого render'а — start-bundle меньше, time-to-interactive быстрее.
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
if (sentryDsn) {
  void import('@sentry/react').then((Sentry) => {
    Sentry.init({
      dsn: sentryDsn,
      environment: import.meta.env.VITE_SENTRY_ENV || 'dev',
      release: import.meta.env.VITE_APP_VERSION as string | undefined,
      tracesSampleRate: 0.1,
    });
  });
}

// Чанк прошлой сборки не загрузился: мини-апп открыт со старой версией, а следующий
// экран просит файл, которого больше нет. Перезагружаемся один раз на версию, и человек
// оказывается на новой сборке сам, без кнопки «Сбросить кэш». Данные входа Telegram
// переживают перезагрузку: SDK хранит их в sessionStorage.
window.addEventListener('vite:preloadError', (event) => {
  const key = `vpnf-reload:${import.meta.env.VITE_APP_VERSION ?? 'dev'}`;
  try {
    if (window.sessionStorage.getItem(key)) return;
    window.sessionStorage.setItem(key, '1');
  } catch {
    return;
  }
  event.preventDefault();
  window.location.reload();
});

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPreloadDelay: 50,
  defaultPreloadStaleTime: 0,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// Тема кабинета — фиксированная тёмная (см. index.css), под тему юзера Telegram
// не адаптируемся: дизайн заточен под тёмную радиалку.

// Eager prefetch: гасим waterfall auth → bots → stats (раньше 3 RTT по тоннелю).
// Все 3 запроса стартуют параллельно сразу после авторизации, к mount'у /dashboard
// данные уже в кэше → нет скелетона, мгновенный показ.
//
// `api.me()` делит один промис между ['partner-me'] и ['partner'] queryKey
// (два таба используют разные keys, чтобы не зависеть друг от друга).
const partnerPromise = api.me();
const botsPromise = api.bots();
const statsPromise = api.dashboardStats(30);
queryClient.prefetchQuery({ queryKey: ['partner-me'], queryFn: () => partnerPromise }).catch(() => {});
queryClient.prefetchQuery({ queryKey: ['partner'], queryFn: () => partnerPromise }).catch(() => {});
queryClient.prefetchQuery({ queryKey: ['bots'], queryFn: () => botsPromise }).catch(() => {});
queryClient
  // Ключ тот же, что у сводки (период 30 дней, без фильтра по боту): до 17.10.2026 он
  // расходился, и заранее загруженные цифры сводка не находила.
  .prefetchQuery({ queryKey: ['partner-dashboard-stats', 30, null], queryFn: () => statsPromise })
  .catch(() => {});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    {/* «Уменьшить движение» для анимаций motion: CSS-медиазапрос их не останавливает,
        они идут инлайн-стилями. С "user" сдвиги и масштаб гаснут, прозрачность остаётся. */}
    <MotionConfig reducedMotion="user">
      <ErrorBoundary>
        <RouterProvider router={router} />
      </ErrorBoundary>
    </MotionConfig>
  </StrictMode>,
);
